/**
 * Pure geometry shared by the ZPL sticker and the colour posters: a layout
 * profile in millimetres is turned into pixel rectangles for a given scale.
 * No DOM here; text measuring is injected.
 */

export interface IMenuQrRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface IMenuQrPaddingMm {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface IMenuQrLayoutProfile {
  widthMm: number;
  heightMm: number;
  padding: IMenuQrPaddingMm;
  offsetXMm: number;
  offsetYMm: number;
  qrScale: number;
  titleFontSizeMm: number;
  titleGapMm: number;
  logoSizeMm: number;
  logoOffsetMm: number;
  /**
   * When true and a footer is laid out, the logo box becomes a square of the
   * footer's height, top-aligned with it (one row: logo | steps), placed
   * `logoOffsetMm` from the content's left edge; `logoSizeMm` is then ignored.
   */
  logoMatchesFooter?: boolean;
  footerWidthMm: number;
  footerOffsetBottomMm: number;
}

/** Centered text anchored at its top edge (canvas textBaseline 'top'). */
export interface IMenuQrTextSpec {
  centerX: number;
  top: number;
  fontPx: number;
  maxWidth: number;
}

export interface IMenuQrLayout {
  width: number;
  height: number;
  content: IMenuQrRect;
  qr: IMenuQrRect;
  title: IMenuQrTextSpec;
  logo: IMenuQrRect | null;
  footer: IMenuQrRect | null;
}

export type TMenuQrLayoutErrorCode = 'empty-area' | 'no-room-for-qr';

export class MenuQrLayoutError extends Error {
  constructor(public readonly code: TMenuQrLayoutErrorCode) {
    super(`Menu QR layout error: ${code}`);
    this.name = 'MenuQrLayoutError';
  }
}

/** Returns the rendered width of the title at the given font size. */
export type TMeasureTextWidth = (fontPx: number) => number;

export interface IMenuQrLayoutInput {
  profile: IMenuQrLayoutProfile;
  pxPerMm: number;
  hasLogo: boolean;
  hasFooter: boolean;
  measureTitleWidth: TMeasureTextWidth;
}

/** Steps footer sprite (public/assets/img/qr/qrbottom.png): 567x208, icons in rows 0..163. */
const FOOTER_SPRITE_WIDTH = 567;
const FOOTER_SPRITE_HEIGHT = 208;
const FOOTER_STRIP_HEIGHT = 164;
const FOOTER_LABEL_CENTERS = [78.5, 283.5, 487];
const FOOTER_LABEL_BASELINE = 197;
const FOOTER_LABEL_FONT_RATIO = 0.15;
const FOOTER_LABEL_MAX_WIDTH_RATIO = 0.3;

export const MENU_QR_FOOTER_ASPECT = FOOTER_SPRITE_HEIGHT / FOOTER_SPRITE_WIDTH;

export const MENU_QR_FOOTER_STRIP_SOURCE: IMenuQrRect = {
  x: 0,
  y: 0,
  width: FOOTER_SPRITE_WIDTH,
  height: FOOTER_STRIP_HEIGHT,
};

/** Centered label anchored at its baseline (canvas textBaseline 'alphabetic'). */
export interface IMenuQrFooterLabelSpec {
  centerX: number;
  baselineY: number;
  fontPx: number;
  maxWidth: number;
}

export interface IMenuQrFooterGeometry {
  stripSource: IMenuQrRect;
  stripDest: IMenuQrRect;
  labels: IMenuQrFooterLabelSpec[];
}

/**
 * Shrinks a font size until the text fits, never below max(8 px, half the
 * preferred size).
 */
export function fitFontSize(
  preferredPx: number,
  maxWidth: number,
  measure: TMeasureTextWidth
): number {
  let current = Math.max(1, Math.round(preferredPx));
  const minimum = Math.max(8, Math.round(current * 0.5));

  while (current > minimum) {
    if (measure(current) <= maxWidth) return current;
    current--;
  }

  return minimum;
}

export function layoutMenuQr(input: IMenuQrLayoutInput): IMenuQrLayout {
  const { profile, pxPerMm } = input;
  const len = (mm: number) => Math.max(1, Math.round(mm * pxPerMm));
  const offset = (mm: number) => Math.round(mm * pxPerMm);

  const width = len(profile.widthMm);
  const height = len(profile.heightMm);
  const paddingTop = len(profile.padding.top);
  const paddingRight = len(profile.padding.right);
  const paddingBottom = len(profile.padding.bottom);
  const paddingLeft = len(profile.padding.left);

  const content: IMenuQrRect = {
    x: paddingLeft + offset(profile.offsetXMm),
    y: paddingTop + offset(profile.offsetYMm),
    width: width - paddingLeft - paddingRight,
    height: height - paddingTop - paddingBottom,
  };

  if (content.width <= 0 || content.height <= 0) {
    throw new MenuQrLayoutError('empty-area');
  }

  let footer: IMenuQrRect | null = null;

  if (input.hasFooter && profile.footerWidthMm > 0) {
    const footerWidth = Math.min(len(profile.footerWidthMm), content.width);
    const footerHeight = Math.max(
      1,
      Math.round(MENU_QR_FOOTER_ASPECT * footerWidth)
    );

    footer = {
      x: Math.round(content.x + (content.width - footerWidth) / 2),
      y: Math.round(
        content.y +
          content.height -
          footerHeight -
          len(profile.footerOffsetBottomMm)
      ),
      width: footerWidth,
      height: footerHeight,
    };
  }

  const titleGap = len(profile.titleGapMm);
  const preferredTitleFont = len(profile.titleFontSizeMm);
  const blockBottom = footer ? footer.y - titleGap : content.y + content.height;
  const qrMaxSize = Math.min(
    content.width,
    blockBottom - content.y - titleGap - preferredTitleFont
  );

  if (qrMaxSize <= 0) {
    throw new MenuQrLayoutError('no-room-for-qr');
  }

  const qrSize = Math.max(1, Math.round(qrMaxSize * profile.qrScale));
  const titleFont = fitFontSize(
    preferredTitleFont,
    content.width,
    input.measureTitleWidth
  );
  const blockHeight = qrSize + titleGap + titleFont;
  const blockTop = Math.round(
    content.y + Math.max(0, (blockBottom - content.y - blockHeight) / 2)
  );

  const qr: IMenuQrRect = {
    x: Math.round(content.x + (content.width - qrSize) / 2),
    y: blockTop,
    width: qrSize,
    height: qrSize,
  };

  const title: IMenuQrTextSpec = {
    centerX: Math.round(content.x + content.width / 2),
    top: qr.y + qr.height + titleGap,
    fontPx: titleFont,
    maxWidth: content.width,
  };

  let logo: IMenuQrRect | null = null;

  if (input.hasLogo && profile.logoMatchesFooter && footer) {
    logo = {
      x: content.x + len(profile.logoOffsetMm),
      y: footer.y,
      width: footer.height,
      height: footer.height,
    };
  } else if (input.hasLogo && profile.logoSizeMm > 0) {
    const logoSize = len(profile.logoSizeMm);
    const logoOffset = len(profile.logoOffsetMm);

    logo = {
      x: content.x + logoOffset,
      y: content.y + content.height - logoOffset - logoSize,
      width: logoSize,
      height: logoSize,
    };
  }

  return { width, height, content, qr, title, logo, footer };
}

/** Places the sprite icon strip and the three localized labels inside a footer rect. */
export function layoutMenuQrFooter(footer: IMenuQrRect): IMenuQrFooterGeometry {
  const stripDest: IMenuQrRect = {
    x: footer.x,
    y: footer.y,
    width: footer.width,
    height: Math.round(
      (footer.height * FOOTER_STRIP_HEIGHT) / FOOTER_SPRITE_HEIGHT
    ),
  };
  const baselineY =
    footer.y + (footer.height * FOOTER_LABEL_BASELINE) / FOOTER_SPRITE_HEIGHT;
  const fontPx = Math.max(
    8,
    Math.round(footer.height * FOOTER_LABEL_FONT_RATIO)
  );
  const maxWidth = Math.round(footer.width * FOOTER_LABEL_MAX_WIDTH_RATIO);

  return {
    stripSource: MENU_QR_FOOTER_STRIP_SOURCE,
    stripDest,
    labels: FOOTER_LABEL_CENTERS.map((center) => ({
      centerX: footer.x + (footer.width * center) / FOOTER_SPRITE_WIDTH,
      baselineY,
      fontPx,
      maxWidth,
    })),
  };
}
